"use client";

import { useEffect, useMemo, useState } from "react";
import axios from "@/lib/api";
import toast from "react-hot-toast";

/* ----------------------------------------------------------------
   HARD-CODED FOUR-REGION SCHEME + DISTRICTS (as requested)
-----------------------------------------------------------------*/
type Region = "Northern" | "Central" | "Eastern" | "Southern";

const REGIONS: Region[] = ["Northern", "Central", "Eastern", "Southern"];

const REGION_TO_DISTRICTS: Record<Region, string[]> = {
  Northern: [
    "Chitipa",
    "Karonga",
    "Rumphi",
    "Nkhata Bay",
    "Mzuzu",
    "Likoma",
    "Mzimba",
  ],
  Central: [
    "Kasungu",
    "Nkhotakota",
    "Ntchisi",
    "Dowa",
    "Salima",
    "Lilongwe",
    "Mchinji",
    "Dedza",
    "Ntcheu",
  ],
  Eastern: ["Mangochi", "Machinga", "Balaka", "Zomba"],
  Southern: [
    "Blantyre",
    "Thyolo",
    "Chiradzulu",
    "Mwanza",
    "Neno",
    "Mulanje",
    "Phalombe",
    "Chikwawa",
    "Nsanje",
  ],
};

const SCHEME = "withEastern4";

/* ----------------------------------------------------------------
   TYPES
-----------------------------------------------------------------*/
interface Car {
  _id: string;
  name: string;
  model?: string;
  image?: string;
}

type RatesByRegion = Record<string, Record<string, number>>; // { [region]: { [district]: price } }

/* ----------------------------------------------------------------
   HELPERS
-----------------------------------------------------------------*/
const formatCurrency = (value: string | number): string => {
  if (value === null || value === undefined || value === "") return "";
  const n =
    typeof value === "number"
      ? value
      : parseInt(value.toString().replace(/[^0-9]/g, ""), 10);
  if (isNaN(n)) return "";
  return "MK " + n.toLocaleString("en-MW");
};

/* ----------------------------------------------------------------
   COMPONENT
-----------------------------------------------------------------*/
export default function EditRegionRatesPage() {
  const [cars, setCars] = useState<Car[]>([]);
  const [selectedCarId, setSelectedCarId] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<Region>("Eastern"); // default to Eastern per your scheme
  const [ratesByRegion, setRatesByRegion] = useState<RatesByRegion>({});
  const [loading, setLoading] = useState(false);

  const selectedDistricts = useMemo(
    () => REGION_TO_DISTRICTS[selectedRegion] || [],
    [selectedRegion]
  );

  const selectedCar = useMemo(
    () => cars.find((c) => c._id === selectedCarId) || null,
    [cars, selectedCarId]
  );

  /* ------------------------ LOAD CARS ------------------------ */
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("/cars"); // same base style as your file
        const data: Car[] = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : res.data?.cars ?? [];
        setCars(data);

        if (data.length > 0) {
          setSelectedCarId(data[0]._id);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load cars");
      }
    })();
  }, []);

  /* -------------- LOAD RATES FOR SELECTED CAR --------------- */
  useEffect(() => {
    if (!selectedCarId) return;
    (async () => {
      setLoading(true);
      try {
        // GET /cars/:carId/rates -> { scheme, carId, ratesByRegion }
        const res = await axios.get(`/cars/${selectedCarId}/rates`, {
          params: { scheme: SCHEME },
        });
        const serverRates: RatesByRegion = res.data?.ratesByRegion || {};
        // ensure all keys exist so UI renders consistently
        const complete: RatesByRegion = { ...serverRates };
        REGIONS.forEach((r) => {
          if (!complete[r]) complete[r] = {};
          REGION_TO_DISTRICTS[r].forEach((d) => {
            if (typeof complete[r][d] !== "number") complete[r][d] = 0;
          });
        });
        setRatesByRegion(complete);
      } catch (err) {
        console.error(err);
        // fallback: initialize blank grid for all regions/districts
        const blank: RatesByRegion = {};
        REGIONS.forEach((r) => {
          blank[r] = {};
          REGION_TO_DISTRICTS[r].forEach((d) => (blank[r][d] = 0));
        });
        setRatesByRegion(blank);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedCarId]);

  /* ---------------------- CHANGE HANDLERS -------------------- */
  const handleRateChange = (district: string, value: number) => {
    setRatesByRegion((prev) => {
      const copy = { ...prev };
      const regionMap = { ...(copy[selectedRegion] || {}) };
      regionMap[district] = value;
      copy[selectedRegion] = regionMap;
      return copy;
    });
  };

  const handleSave = async () => {
    if (!selectedCarId) {
      toast.error("Please select a car");
      return;
    }

    setLoading(true);
    try {
      const items: { region: string; district: string; price: number }[] = [];

      // Only send items for the currently selected region (cleaner UX).
      // If you want to save ALL regions in one go, loop REGIONS instead.
      const map = ratesByRegion[selectedRegion] || {};
      for (const d of REGION_TO_DISTRICTS[selectedRegion]) {
        const price = Number(map[d] ?? 0);
        if (!isFinite(price) || price < 0) continue;
        items.push({ region: selectedRegion, district: d, price });
      }

      await axios.put(`/cars/${selectedCarId}/rates`, {
        scheme: SCHEME,
        items,
      });

      toast.success("Rates saved successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save rates");
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------- UI --------------------------- */
  return (
    <div className="max-w-5xl px-6 py-6 mx-auto space-y-6">
      <h1 className="text-xl font-bold text-blue-900">
        Edit Region & District Rates
      </h1>

      {/* Car selector */}
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="block mb-1 text-sm font-semibold text-blue-800">
            Select Car
          </label>
          <select
            value={selectedCarId}
            onChange={(e) => setSelectedCarId(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          >
            {cars.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name} {c.model ? `(${c.model})` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Region selector */}
        <div>
          <label className="block mb-1 text-sm font-semibold text-blue-800">
            Select Region
          </label>
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value as Region)}
            className="w-full px-3 py-2 border rounded"
          >
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* District rate editor */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead className="bg-blue-100">
            <tr>
              <th className="p-2 text-left border">Region</th>
              <th className="p-2 text-left border">District</th>
              <th className="p-2 text-left border">Price (MWK)</th>
              <th className="p-2 text-left border">Preview</th>
            </tr>
          </thead>
          <tbody>
            {selectedDistricts.map((district) => {
              const current = ratesByRegion[selectedRegion]?.[district] ?? 0;
              return (
                <tr key={district} className="hover:bg-gray-50">
                  <td className="p-2 border">{selectedRegion}</td>
                  <td className="p-2 border">{district}</td>
                  <td className="p-2 border">
                    <input
                      type="number"
                      min={0}
                      value={current}
                      onChange={(e) =>
                        handleRateChange(district, Number(e.target.value))
                      }
                      className="w-full px-2 py-1 border rounded"
                    />
                  </td>
                  <td className="p-2 text-gray-600 border">
                    {formatCurrency(current)}
                  </td>
                </tr>
              );
            })}
            {selectedDistricts.length === 0 && (
              <tr>
                <td colSpan={4} className="p-4 text-center text-gray-500">
                  No districts for this region.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {selectedCar ? (
            <>
              Editing rates for: <strong>{selectedCar.name}</strong>{" "}
              {selectedCar.model ? `(${selectedCar.model})` : ""} —{" "}
              <strong>{selectedRegion}</strong> region
            </>
          ) : (
            "Select a car to begin"
          )}
        </div>

        <button
          onClick={handleSave}
          disabled={loading || !selectedCarId}
          className="px-6 py-2 font-semibold text-white transition bg-blue-900 rounded hover:bg-blue-800 disabled:opacity-60"
        >
          {loading ? "Saving..." : "Save Rates"}
        </button>
      </div>
    </div>
  );
}
